/**
 * Keeps the scheme reference clinical's pickers use in step with the one Connect publishes: the cached copy straight
 * away, then the server's when its hash moved. The bundled list stands in only while neither has one, and is never
 * cached (tasks/scheme-linking-central.md).
 */

import type { LearnedMatch } from './scheme-match';

export interface SchemeOptionEntry {
  label?: string;
  planCode: string;
  planName?: string;
  optionCode: string;
  optionName?: string;
}

export interface SchemeEntry {
  code: string;
  name: string;
  options: SchemeOptionEntry[];
  /** Confirmed MediKredit switching codes; null when the scheme has none (claims go on paper or wait). */
  switching?: unknown;
}

export interface SchemeReferenceData {
  schemes: SchemeEntry[];
  retiredSwitchingOptions?: Record<string, unknown>;
  /** Typed text people linked often enough, for the admission scanner (scheme-match.ts `learnedPick`). */
  learned?: LearnedMatch[];
}

export interface SchemeLoaderDeps {
  readCache(): Promise<{ hash: string; data: SchemeReferenceData } | null>;
  writeCache(hash: string, data: SchemeReferenceData): Promise<void>;
  fetchHash(): Promise<string | null>;
  fetchReference(): Promise<{ hash: string | null; data: SchemeReferenceData | null }>;
  fetchFallback(): Promise<SchemeReferenceData | null>;
  show(data: SchemeReferenceData): void;
}

export class SchemeReferenceLoader {
  private hash: string | null = null;
  private hasData = false;
  private inflight: Promise<void> | null = null;

  constructor(private readonly deps: SchemeLoaderDeps) {}

  async load(): Promise<void> {
    const cached = await this.deps.readCache().catch(() => null);
    if (cached?.data?.schemes?.length) this.use(cached.hash, cached.data);
    await this.refresh();
  }

  refresh(): Promise<void> {
    this.inflight ??= this.check().finally(() => (this.inflight = null));
    return this.inflight;
  }

  private async check(): Promise<void> {
    try {
      const hash = await this.deps.fetchHash();
      if (!hash) return await this.fallbackIfEmpty();
      if (hash === this.hash) return;
      const body = await this.deps.fetchReference();
      if (!body.hash || !body.data?.schemes?.length) return await this.fallbackIfEmpty();
      this.use(body.hash, body.data);
      await this.deps.writeCache(body.hash, body.data).catch(() => undefined);
    } catch {
      await this.fallbackIfEmpty();
    }
  }

  private use(hash: string | null, data: SchemeReferenceData): void {
    this.hash = hash;
    this.hasData = true;
    this.deps.show(data);
  }

  private async fallbackIfEmpty(): Promise<void> {
    if (this.hasData) return;
    const data = await this.deps.fetchFallback().catch(() => null);
    if (!data?.schemes?.length) return;
    this.hasData = true;
    this.deps.show(data);
  }
}
